import { useAuth } from "@/hooks/use-auth";
import { Loader2, AlertCircle } from "lucide-react";
import { Redirect, Route } from "wouter";
import { ReactElement, ComponentType, LazyExoticComponent, memo, useMemo } from "react";
import AppLayout from "@/components/layout/app-layout";

interface ProtectedRouteProps {
  path: string;
  component: ComponentType<any> | LazyExoticComponent<ComponentType<any>>;
  allowedRoles?: string[];
}

export const ProtectedRoute = memo(function ProtectedRoute({
  path,
  component: Component,
  allowedRoles,
}: ProtectedRouteProps) {
  const { user, isLoading, error } = useAuth();
  
  // Memoize the access check to avoid recalculating on every render
  const hasAccess = useMemo(() => {
    if (!user) return false;
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return allowedRoles.includes(user.role);
  }, [user, allowedRoles]);

  if (isLoading) {
    return (
      <Route path={path}>
        <AppLayout>
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-600">Authenticating...</p>
            </div>
          </div>
        </AppLayout>
      </Route>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <Route path={path}>
        <Redirect to="/" />
      </Route>
    );
  }

  // Check role-based access if roles are specified
  if (!hasAccess) {
    return (
      <Route path={path}>
        <AppLayout>
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] gap-4">
            <AlertCircle className="h-16 w-16 text-red-500" />
            <h1 className="text-2xl font-bold">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to access this page.</p>
            <p className="text-sm text-gray-500">
              Your role: {user.role}, Required: {allowedRoles?.join(', ')}
            </p>
            <button 
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
              onClick={() => window.history.back()}
            >
              Go Back
            </button>
          </div>
        </AppLayout>
      </Route>
    );
  }

  // User is authenticated and has the right role (or no specific role required)
  return <Route path={path} component={Component} />;
});

// Route exclusively for admins
export const AdminRoute = memo(function AdminRoute({
  path,
  component: Component,
}: {
  path: string;
  component: ComponentType<any> | LazyExoticComponent<ComponentType<any>>;
}) {
  return <ProtectedRoute path={path} component={Component} allowedRoles={["admin"]} />;
});

// Route exclusively for auditors
export const AuditorRoute = memo(function AuditorRoute({
  path,
  component: Component,
}: {
  path: string;
  component: ComponentType<any> | LazyExoticComponent<ComponentType<any>>;
}) {
  return <ProtectedRoute path={path} component={Component} allowedRoles={["auditor"]} />;
});
